import { useMemo } from 'react';
import type { AxiosInstance } from 'axios';
import { useAxios } from 'hooks/useAxios';

/**
 * The transport for the deal form's two entitlement lookups: the shared `useAxios` instance with its
 * notification removed, and nothing else changed.
 *
 * **Why this exists.** `GET /clients/:id/portfolio-entitlements` and
 * `GET /clients/:id/contracts/:contractId/entitlements` only *enrich* the "Report Type Requested"
 * picker — when either fails the form falls back to the full report-type catalog and the user can
 * still create or edit the deal. But `useAxios`'s response interceptor raises an antd
 * `notification.warning` on every transport failure, carrying the raw server text, so a non-blocking
 * enrichment miss reads as a blocker mid-form: an environment whose API build predates the route
 * answers Nest's own 404 and the user is shown
 * `Cannot GET /v2/clients/478/portfolio-entitlements` (VE-2622). `useAxios` is a protected file
 * serving every other client in the app, so the toast is removed for this client only — the same
 * reasoning, and the same mechanism, as {@link useDealDeskBulkAxios}.
 *
 * **It is the shared instance, not a copy of it.** `useAxios`'s `useMemo` builds a fresh
 * `AxiosInstance` per call site, so the instance handed back here belongs to this hook and can be
 * adjusted without reaching any other client — including the plain `useAxios` instance `useNewDeal`
 * builds for its other endpoints. Everything on the request side is therefore reproduced **by
 * construction** rather than by duplication: base URL, the Auth0 bearer token, `Accept`,
 * `Content-Type` (and its FormData exemption), `Cache-Control`, and every axios default.
 *
 * **The response side is replaced, and the replacement is identical minus the toast.** `clear()`
 * drops the shared handler; the handler put back `return`s the error exactly as the shared one does.
 * Fulfilling rather than rejecting is the contract `useNewDeal`'s readers are written against — they
 * do `const res = await axios.get(...); return res.data`, so the failure must arrive as a value whose
 * `.data` is undefined. Rejecting would throw into every one of those call sites.
 *
 * **What it cannot reproduce.** `clear()` removes the whole response chain, so a *success* handler
 * added to `useAxios` later (response unwrapping, logging, retry) would be dropped here silently.
 * @param {string} baseURL - The API base URL, as `useAxios` takes it.
 * @returns {{ axios: AxiosInstance }} The shared instance, minus the failure notification.
 * @example
 * const { axios: entitlementsAxios } = useEntitlementsAxios(BASE_URL_V2);
 */
export const useEntitlementsAxios = (baseURL: string): { axios: AxiosInstance } => {
  const { axios } = useAxios(baseURL);

  const silenced = useMemo(() => {
    // Clear-then-add, in that order, so a re-run of this factory cannot stack a second handler.
    axios.interceptors.response.clear();
    // `return error` — FULFILS with it, exactly as the shared handler does, minus the notification.
    axios.interceptors.response.use(undefined, (error: unknown) => error);
    return axios;
  }, [axios]);

  return { axios: silenced };
};
